import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    NCoreUIKitLocalize,
    type ChartConfig,
    NCoreUIKitTheme,
    LineChart,
    Separator,
    Avatar,
    Button,
    Table,
    Badge,
    Alert,
    Empty,
    Chart,
    Item,
    Card,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    MessageSquare,
    ArrowDown,
    Calendar,
    Download,
    ArrowUp,
    Check,
    Bell
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const BellIcon = createKitIcon(Bell);

const DownloadIcon = createKitIcon(Download);

const MessageIcon = createKitIcon(MessageSquare);

const CalendarIcon = createKitIcon(Calendar);

const CheckIcon = createKitIcon(Check);

const ArrowDownIcon = createKitIcon(ArrowDown);

const ArrowUpIcon = createKitIcon(ArrowUp);

const TABS = [
    "results",
    "appointments",
    "prescriptions",
    "messages"
];

const TAB_COUNTS: Record<string, number | undefined> = {
    appointments: 1,
    messages: 2
};

const TESTS = [
    {
        id: "hemoglobin",
        unit: "g/dL",
        value: 13.4,
        high: 15.5,
        low: 12
    },
    {
        id: "ferritin",
        unit: "ng/mL",
        high: 150,
        value: 11,
        low: 15
    },
    {
        unit: "pg/mL",
        value: 412,
        high: 900,
        id: "b12",
        low: 200
    },
    {
        id: "vitaminD",
        unit: "ng/mL",
        high: 100,
        value: 18,
        low: 30
    },
    {
        unit: "mIU/L",
        value: 2.1,
        id: "tsh",
        low: 0.4,
        high: 4
    },
    {
        id: "glucose",
        unit: "mg/dL",
        high: 100,
        value: 92,
        low: 70
    }
];

const VITAMIN_D = [
    26,
    22,
    21,
    19,
    20,
    24,
    31,
    34,
    33,
    28,
    22,
    18
];

const VITAMIN_D_LOW_LIMIT = 30;

const CHART_ROWS = VITAMIN_D.map((value, index) => ({
    date: new Date(2025, 10 + index, 1).toISOString(),
    limit: VITAMIN_D_LOW_LIMIT,
    value
}));

const APPOINTMENT_DATE = new Date(2026, 10, 17, 10, 30);

const COMMENT_DATE = new Date(2026, 9, 31, 17, 20);

const SAMPLE_DATE = new Date(2026, 9, 28, 8, 15);

const RESULT_DATE = new Date(2026, 9, 30);

const SCALE_PADDING = 0.6;

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    footer: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    track: {
        justifyContent: "center",
        width: "100%"
    },
    row: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    patient: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    band: {
        position: "absolute",
        height: "100%"
    },
    marker: {
        position: "absolute",
        borderRadius: 9999
    },
    wrap: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    page: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    },
    legendDot: {
        borderRadius: 9999
    },
    scroll: {
        minHeight: "100%"
    },
    cell: {
        flexShrink: 1,
        minWidth: 0
    },
    main: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    columns: {
        width: "100%"
    },
    flex: {
        flexBasis: 0,
        flexGrow: 1
    },
    side: {
        flexGrow: 0
    },
    grow: {
        flexGrow: 1
    }
});

const handlePress = () => undefined;

const Health01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        tab,
        setTab
    ] = useState("results");

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const numberFormat = new Intl.NumberFormat(activeLocale);

    const monthFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short"
    });

    const dateFormat = new Intl.DateTimeFormat(activeLocale, {
        day: "numeric",
        month: "short",
        year: "numeric"
    });

    const dateTimeFormat = new Intl.DateTimeFormat(activeLocale, {
        minute: "2-digit",
        hour: "2-digit",
        day: "numeric",
        month: "short",
        year: "numeric"
    });

    const weekdayFormat = new Intl.DateTimeFormat(activeLocale, {
        minute: "2-digit",
        hour: "2-digit",
        weekday: "long",
        month: "short",
        day: "numeric"
    });

    const chartConfig: ChartConfig = {
        value: {
            title: t("blocks.health01.chartSeries"),
            colorIndex: 1
        },
        limit: {
            title: t("blocks.health01.chartLimit"),
            colorIndex: 3
        }
    };

    const getStatus = (test: typeof TESTS[number]) => {
        if(test.value < test.low) {
            return "low";
        }

        if(test.value > test.high) {
            return "high";
        }

        return "normal";
    };

    const outOfRangeCount = TESTS.filter((test) => getStatus(test) !== "normal").length;

    const renderRange = (test: typeof TESTS[number]) => {
        const status = getStatus(test);

        const span = test.high - test.low;

        const min = test.low - span * SCALE_PADDING;

        const max = test.high + span * SCALE_PADDING;

        const toPercent = (value: number) => `${Math.min(Math.max((value - min) / (max - min), 0), 1) * 100}%` as const;

        const markerSize = spaces.spacingMd;

        return <View
            style={[
                stylesheet.cell,
                stylesheet.flex,
                {
                    minWidth: shapeConfigs.fieldMinWidth,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <View
                style={[
                    stylesheet.track,
                    {
                        backgroundColor: colors.content.container.mid,
                        borderRadius: radiuses.full,
                        height: spaces.spacingSm
                    }
                ]}
                accessibilityLabel={t("blocks.health01.rangeLabel", [
                    numberFormat.format(test.low),
                    numberFormat.format(test.high),
                    test.unit
                ])}
            >
                <View
                    style={[
                        stylesheet.band,
                        {
                            width: `${(test.high - test.low) / (max - min) * 100}%`,
                            backgroundColor: colors.content.border.success,
                            borderRadius: radiuses.full,
                            left: toPercent(test.low)
                        }
                    ]}
                />
                <View
                    style={[
                        stylesheet.marker,
                        {
                            backgroundColor: status === "normal" ? colors.content.icon.high : colors.content.icon.warning,
                            borderColor: colors.content.container.default,
                            borderWidth: borders.line * 2,
                            marginLeft: -markerSize / 2,
                            left: toPercent(test.value),
                            height: markerSize,
                            width: markerSize
                        }
                    ]}
                />
            </View>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.health01.normalRange", [
                    numberFormat.format(test.low),
                    numberFormat.format(test.high),
                    test.unit
                ])}
            </Text>
        </View>;
    };

    const statusIcons = {
        low: ArrowDownIcon,
        normal: CheckIcon,
        high: ArrowUpIcon
    };

    const renderTest = (test: typeof TESTS[number]) => {
        const status = getStatus(test);

        return <Table.Row
            key={test.id}
        >
            <Table.Cell>
                <View
                    style={stylesheet.cell}
                >
                    <Text
                        variant="titleSmallSize"
                        color="high"
                    >
                        {t(`blocks.health01.test_${test.id}`)}
                    </Text>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t(`blocks.health01.test_${test.id}_note`)}
                    </Text>
                </View>
            </Table.Cell>
            <Table.Cell
                isNumeric={true}
            >
                <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {numberFormat.format(test.value)}
                </Text>
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {test.unit}
                </Text>
            </Table.Cell>
            <Table.Cell>
                {renderRange(test)}
            </Table.Cell>
            <Table.Cell>
                <Badge
                    type={status === "normal" ? "success" : "warning"}
                    title={t(`blocks.health01.status_${status}`)}
                    icon={statusIcons[status]}
                    variant="tonal"
                />
            </Table.Cell>
        </Table.Row>;
    };

    const renderResults = () => <View
        style={{
            gap: spaces.spacingMd
        }}
    >
        <View
            style={[
                stylesheet.columns,
                {
                    flexDirection: isWide ? "row" : "column",
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Card
                style={stylesheet.main}
                variant="outline"
            >
                <Card.Header>
                    <Card.Title>
                        {t("blocks.health01.panelTitle")}
                    </Card.Title>
                    <Card.Description>
                        {t("blocks.health01.panelMeta", [
                            dateTimeFormat.format(SAMPLE_DATE),
                            dateFormat.format(RESULT_DATE)
                        ])}
                    </Card.Description>
                </Card.Header>
                <Card.Content>
                    <View
                        style={{
                            gap: spaces.spacingSm
                        }}
                    >
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <View
                                    style={{
                                        backgroundColor: colors.content.border.success,
                                        borderRadius: radiuses.full,
                                        height: spaces.spacingSm,
                                        width: spaces.spacingLg
                                    }}
                                />
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.health01.legendNormal")}
                                </Text>
                            </View>
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <View
                                    style={[
                                        stylesheet.legendDot,
                                        {
                                            backgroundColor: colors.content.icon.high,
                                            height: spaces.spacingMd,
                                            width: spaces.spacingMd
                                        }
                                    ]}
                                />
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.health01.legendValue")}
                                </Text>
                            </View>
                        </View>
                    </View>
                </Card.Content>
                <Card.Content
                    isFlush={true}
                >
                    <Table
                        caption={t("blocks.health01.panelTitle")}
                        isCaptionHidden={true}
                        size="large"
                    >
                        <Table.Header>
                            <Table.Row>
                                <Table.Head>
                                    {t("blocks.health01.col_test")}
                                </Table.Head>
                                <Table.Head
                                    isNumeric={true}
                                >
                                    {t("blocks.health01.col_result")}
                                </Table.Head>
                                <Table.Head>
                                    {t("blocks.health01.col_range")}
                                </Table.Head>
                                <Table.Head>
                                    {t("blocks.health01.col_status")}
                                </Table.Head>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {TESTS.map(renderTest)}
                        </Table.Body>
                    </Table>
                </Card.Content>
                <Card.Footer>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.health01.disclaimer")}
                    </Text>
                </Card.Footer>
            </Card>
            <View
                style={[
                    stylesheet.side,
                    {
                        width: isWide ? shapeConfigs.cardWidth : "100%",
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Chart
                    description={t("blocks.health01.chartDescription", [
                        numberFormat.format(18)
                    ])}
                    title={t("blocks.health01.chartTitle")}
                    spreadBehaviour="stretch"
                    config={chartConfig}
                >
                    <LineChart
                        categoryFormatter={(value) => monthFormat.format(new Date(String(value)))}
                        categoryTitle={t("blocks.health01.month")}
                        valueFormatter={numberFormat.format}
                        endLabelBehaviour="none"
                        categoryKey="date"
                        data={CHART_ROWS}
                        plotSize="medium"
                        curve="natural"
                        seriesKeys={[
                            "value",
                            "limit"
                        ]}
                    />
                </Chart>
                <Card
                    variant="outline"
                >
                    <Card.Content>
                        <View
                            style={{
                                gap: spaces.spacingMd
                            }}
                        >
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        gap: spaces.spacingSm,
                                        flexWrap: "nowrap"
                                    }
                                ]}
                            >
                                <Avatar
                                    title={t("blocks.health01.doctor")}
                                    size="small"
                                />
                                <View
                                    style={stylesheet.cell}
                                >
                                    <Text
                                        variant="titleSmallSize"
                                        color="high"
                                    >
                                        {t("blocks.health01.doctor")}
                                    </Text>
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.health01.doctorMeta", [
                                            dateTimeFormat.format(COMMENT_DATE)
                                        ])}
                                    </Text>
                                </View>
                            </View>
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {t("blocks.health01.comment")}
                            </Text>
                            <View
                                style={[
                                    stylesheet.wrap,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Button
                                    title={t("blocks.health01.writeDoctor")}
                                    spreadBehaviour="free"
                                    onPress={handlePress}
                                    icon={MessageIcon}
                                    variant="filled"
                                    type="primary"
                                />
                                <Button
                                    title={t("blocks.health01.viewPrescription")}
                                    spreadBehaviour="free"
                                    onPress={handlePress}
                                    variant="outline"
                                    type="neutral"
                                />
                            </View>
                        </View>
                    </Card.Content>
                </Card>
                <Card
                    variant="outline"
                >
                    <Card.Header>
                        <Card.Title>
                            {t("blocks.health01.nextAppointment")}
                        </Card.Title>
                        <Card.Action>
                            <Badge
                                title={t("blocks.health01.confirmed")}
                                variant="tonal"
                                type="success"
                                size="small"
                            />
                        </Card.Action>
                    </Card.Header>
                    <Card.Content>
                        <View
                            style={{
                                gap: spaces.spacingMd
                            }}
                        >
                            <Item
                                size="xSmall"
                            >
                                <Item.Media
                                    icon={CalendarIcon}
                                    variant="plain"
                                />
                                <Item.Content>
                                    <Item.Title>
                                        {weekdayFormat.format(APPOINTMENT_DATE)}
                                    </Item.Title>
                                    <Item.Description>
                                        {t("blocks.health01.appointmentKind")}
                                    </Item.Description>
                                    <Item.Description>
                                        {t("blocks.health01.appointmentPlace")}
                                    </Item.Description>
                                </Item.Content>
                            </Item>
                            <View
                                style={[
                                    stylesheet.wrap,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Button
                                    title={t("blocks.health01.reschedule")}
                                    onPress={handlePress}
                                    icon={CalendarIcon}
                                    variant="outline"
                                    type="neutral"
                                />
                                <Button
                                    title={t("blocks.health01.cancelAppointment")}
                                    onPress={handlePress}
                                    variant="ghost"
                                    type="neutral"
                                />
                            </View>
                        </View>
                    </Card.Content>
                </Card>
            </View>
        </View>
    </View>;

    const renderOther = () => <Empty
        variant="outline"
    >
        <Empty.Header>
            <Empty.Media
                icon={CalendarIcon}
            />
            <Empty.Title>
                {t(`blocks.health01.tab_${tab}`)}
            </Empty.Title>
            <Empty.Description>
                {t("blocks.health01.otherDescription")}
            </Empty.Description>
        </Empty.Header>
    </Empty>;

    return <ScrollView
        contentContainerStyle={stylesheet.scroll}
    >
        <View
            style={[
                stylesheet.header,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <BrandMark/>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("blocks.health01.portal")}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Badge.Indicator
                    type="danger"
                >
                    <Button
                        accessibilityLabel={t("blocks.health01.notifications")}
                        onPress={handlePress}
                        variant="ghost"
                        icon={BellIcon}
                        type="neutral"
                        size="small"
                    />
                </Badge.Indicator>
                <Avatar
                    title={t("blocks.health01.patient")}
                    size="xSmall"
                />
            </View>
        </View>
        <Separator/>
        <View
            style={[
                stylesheet.page,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.patient,
                    {
                        justifyContent: "space-between",
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.row,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Avatar
                        title={t("blocks.health01.patient")}
                        size="medium"
                    />
                    <View
                        style={stylesheet.cell}
                    >
                        <Text
                            variant="headlineMediumSize"
                            accessibilityRole="header"
                            color="high"
                        >
                            {t("blocks.health01.patient")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.health01.patientMeta")}
                        </Text>
                    </View>
                </View>
                <Button
                    title={t("blocks.health01.downloadPdf")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={DownloadIcon}
                    variant="outline"
                    type="neutral"
                />
            </View>
            <Tabs
                overflowBehaviour="scroll"
                onValueChange={setTab}
                variant="line"
                value={tab}
            >
                <Tabs.List
                    title={t("blocks.health01.tabsTitle")}
                >
                    {
                        TABS.map((key) => <Tabs.Trigger
                            title={t(`blocks.health01.tab_${key}`)}
                            count={TAB_COUNTS[key]}
                            value={key}
                            key={key}
                        />)
                    }
                </Tabs.List>
            </Tabs>
            {
                tab === "results" ?
                    <>
                        <Text
                            variant="headlineSmallSize"
                            accessibilityRole="header"
                            color="high"
                        >
                            {t("blocks.health01.heading")}
                        </Text>
                        <Alert
                            description={t("blocks.health01.summaryDescription")}
                            actions={[
                                {
                                    title: t("blocks.health01.goToComment"),
                                    onPress: handlePress
                                }
                            ]}
                            title={t("blocks.health01.summary", [
                                outOfRangeCount
                            ])}
                            type="info"
                        />
                        {renderResults()}
                    </>
                :
                    renderOther()
            }
        </View>
        <Separator/>
        <View
            style={[
                stylesheet.footer,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.health01.copyright")}
            </Text>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    title={t("blocks.health01.footerPrivacy")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={t("blocks.health01.footerHelp")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
    </ScrollView>;
};
export default Health01;
